import React, { useState, useEffect } from "react";
import { Form, Spinner, Container, Col, Row } from "react-bootstrap";
import Chart from "../Components/Chart/Chart";
import {
  getProgramProgressChart,
  getActiveStudentsPieChart,
  getModuleProgressChart,
  getModuleCompletionStatusChart,
  getQuizTopScoresChart,
  getQuizCompletionRateChart,
} from "../Components/Chart/ChartConfigs";
import { useTranslation } from "react-i18next"; // Importer useTranslation

function Statistics() {
  const { t } = useTranslation(); // Assigner useTranslation à {t}
  const [programs, setPrograms] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [selectedQuiz, setSelectedQuiz] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [statisticsType, setStatisticsType] = useState("");
  const [progressData, setProgressData] = useState(null);
  const [quizData, setQuizData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const programProgressChartConfig = getProgramProgressChart(progressData);
  const activeStudentsPieChartConfig = getActiveStudentsPieChart(progressData);
  const moduleProgressChartConfig = getModuleProgressChart(
    progressData,
    selectedModule
  );
  const moduleCompletionStatusChartConfig = getModuleCompletionStatusChart(
    progressData,
    selectedModule
  );

  useEffect(() => {
    const fetchProgramsCreatedByMe = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/programs/my-programs`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error("Could not fetch programs");
        }

        const data = await response.json();
        setPrograms(data.programs);
        console.log(data.programs);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProgramsCreatedByMe();
  }, []);

  const fetchQuizzes = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/quizzes/${selectedProgram}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Could not fetch quizzes");
      }

      const data = await response.json();
      setQuizzes(data.quizzes);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatisticsTypeChange = (e) => {
    setStatisticsType(e.target.id);

    if (e.target.id === "quiz") {
      // fetch quizzes
      fetchQuizzes();
    }
  };

  const handleSelectedProgramChange = async (e) => {
    const programID = e.target.value;
    setSelectedProgram(programID);
    setStatisticsType("");
    setSelectedModule("");
    setSelectedQuiz("");

    if (programID) {
      setLoading(true);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/user-program-progress/all-users-program-progress/${programID}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          }
        );

        if (!response.ok) throw new Error("Could not fetch progress data");

        const data = await response.json();
        setProgressData(data);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const getSelectedProgramData = () => {
    return programs.find((program) => program._id === selectedProgram);
  };

  const hasStudents = () => {
    const program = getSelectedProgramData();
    return (
      program &&
      program.students_enrolled &&
      program.students_enrolled.length > 0
    );
  };

  const handleSelectedQuiz = (e) => {
    setSelectedQuiz(e.target.value);

    if (e.target.value) {
      setLoading(true);

      fetchQuizData(e.target.value);
    }
  };

  const fetchQuizData = async (quizId) => {
    try {
      setLoading(true);

      const [topScoresResponse, completionRateResponse] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}quiz-attempts/${quizId}/top-scores`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          }
        ),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}quiz-attempts/${quizId}/completion-rate`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          }
        ),
      ]);

      if (!topScoresResponse.ok || !completionRateResponse.ok) {
        throw new Error("Could not fetch quiz data");
      }

      const [topScoresData, completionRateData] = await Promise.all([
        topScoresResponse.json(),
        completionRateResponse.json(),
      ]);

      setQuizData({
        topScores: topScoresData.topScores || [],
        averageScore: topScoresData.averageScore || 0,
        completionRate: completionRateData.completionRate || 0,
        totalAttempts: completionRateData.totalAttempts || 0,
        completedAttempts: completionRateData.completedAttempts || 0,
      });

      console.log("Quiz data loaded:", {
        topScores: topScoresData.topScores?.length || 0,
        averageScore: topScoresData.averageScore,
        completionRate: completionRateData.completionRate,
        attempts: `${completionRateData.completedAttempts}/${completionRateData.totalAttempts}`,
      });
    } catch (error) {
      console.error("Error fetching quiz data:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-4">
      <div className="d-flex gap-4 justify-content-center align-items-center">
        <Form.Group controlId="programSelect" className="mb-3 w-25">
          <Form.Select
            value={selectedProgram}
            onChange={handleSelectedProgramChange}
          >
            <option value="">{t("choose_program")}</option>
            {programs &&
              programs.map((program) => (
                <option key={program._id} value={program._id}>
                  {program.title}
                </option>
              ))}
          </Form.Select>
        </Form.Group>
        {selectedProgram && (
          <div className="">
            <p>{t("statistics_prompt")}</p>
            <Form className="d-flex gap-4">
              <Form.Check
                type="radio"
                label={t("program_statistics")}
                name="statisticsType"
                id="program"
                className="mb-2"
                onChange={handleStatisticsTypeChange}
                checked={statisticsType === "program"}
              />
              <Form.Check
                type="radio"
                label={t("module_statistics")}
                name="statisticsType"
                id="module"
                onChange={handleStatisticsTypeChange}
                checked={statisticsType === "module"}
              />
              <Form.Check
                type="radio"
                label={t("quiz_statistics")}
                name="statisticsType"
                id="quiz"
                onChange={handleStatisticsTypeChange}
                checked={statisticsType === "quiz"}
              />
            </Form>
          </div>
        )}

        {statisticsType === "quiz" && selectedProgram && (
          <Form.Group controlId="quizSelect" className="mb-3">
            <Form.Label>{t("choose_quiz")}</Form.Label>
            <Form.Select
              value={selectedQuiz}
              onChange={handleSelectedQuiz}
              disabled={loading}
            >
              <option value="">{t("select_quiz")}</option>
              {quizzes.map((quiz) => (
                <option key={quiz._id} value={quiz._id}>
                  {quiz.header}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {statisticsType === "module" && selectedProgram && (
          <>
            <Form.Group controlId="moduleSelect" className="mb-3">
              <Form.Label>{t("choose_module")}</Form.Label>
              <Form.Select
                value={selectedModule}
                onChange={(e) => setSelectedModule(e.target.value)}
                disabled={loading}
              >
                <option value="">{t("select_module")}</option>
                {programs
                  .find((program) => program._id === selectedProgram)
                  ?.modules?.map((module) => (
                    <option key={module._id} value={module._id}>
                      {module.title}
                    </option>
                  )) || <option disabled>{t("aucun_module")}</option>}
              </Form.Select>
            </Form.Group>
          </>
        )}
      </div>

      {statisticsType === "quiz" && selectedQuiz && (
        <div className="mt-4">
          {loading ? (
            <div className="text-center">
              <Spinner animation="border" />
            </div>
          ) : quizData ? (
            (() => {
              const quizTopScoresChartConfig = getQuizTopScoresChart(quizData);
              const quizCompletionRateChartConfig =
                getQuizCompletionRateChart(quizData);

              return (
                <div className="d-flex flex-wrap justify-content-center gap-4">
                  <div
                    className="chart-container"
                    style={{ height: "450px", width: "50%" }}
                  >
                    <Chart
                      chartType="bar"
                      data={quizTopScoresChartConfig.data}
                      options={quizTopScoresChartConfig.options}
                    />
                  </div>
                  <div
                    className="chart-container"
                    style={{ height: "450px", width: "48%" }}
                  >
                    <Chart
                      chartType="pie"
                      data={quizCompletionRateChartConfig.data}
                      options={quizCompletionRateChartConfig.options}
                    />
                  </div>
                </div>
              );
            })()
          ) : (
            <p className="text-center">{t("no_data_available_quiz")}</p>
          )}
        </div>
      )}

      {statisticsType === "module" && selectedModule && (
        <div className="mt-4 d-flex justify-content-center">
          {loading ? (
            <div className="text-center">
              <Spinner animation="border" />
            </div>
          ) : progressData ? (
            hasStudents() || progressData?.usersProgress?.length > 0 ? (
              <div
                className="d-flex justify-content-center m-5"
                style={{ height: "450px", width: "70%" }}
              >
                <Chart
                  chartType="bar"
                  data={moduleProgressChartConfig.data}
                  options={moduleProgressChartConfig.options}
                />
                <Chart
                  chartType="pie"
                  data={moduleCompletionStatusChartConfig.data}
                  options={moduleCompletionStatusChartConfig.options}
                />
              </div>
            ) : (
              <p className="text-center">{t("no_student_enrolled")}</p>
            )
          ) : (
            <p className="text-center">{t("no_data_available")}</p>
          )}
        </div>
      )}
      {statisticsType === "program" && selectedProgram && (
        <div className="mt-4 d-flex justify-content-center">
          {loading ? (
            <div className="text-center">
              <Spinner animation="border" />
            </div>
          ) : progressData ? (
            hasStudents() || progressData?.usersProgress?.length > 0 ? (
              <div
                className="d-flex justify-content-center m-5"
                style={{ height: "450px", width: "70%" }}
              >
                <Chart
                  chartType="line"
                  data={programProgressChartConfig.data}
                  options={programProgressChartConfig.options}
                />
                <Chart
                  chartType="pie"
                  data={activeStudentsPieChartConfig.data}
                  options={activeStudentsPieChartConfig.options}
                />
              </div>
            ) : (
              <p className="text-center">{t("no_student_enrolled")}</p>
            )
          ) : (
            <p className="text-center">{"no_data_available"}</p>
          )}
        </div>
      )}
    </Container>
  );
}

export default Statistics;
